import React, { useEffect, useState } from 'react';
import { Alert, Button, LoadingSpinner, SubHeader } from 'vetrf-ui';
import { dictApi } from 'api/dict';
import { ObjectOperation, ObjectTypeId } from 'api/dict/params';
import { BackendFileType } from 'api/file/params';
import { FileType } from 'types/types';
import { normalizeFile } from 'utils/fileUtils';
import { ROLES } from 'utils/consts/roles';
import { Timeline } from 'components/Timeline/Timeline';
import { pharmPermissionsApi } from '../../../../api';
import {
  FunctionPermissions,
  MovementData,
  MovementType,
  PharmPermissionDTO,
} from '../../../../types/types';
import { AddMotionModal } from './components/AddMotionModal';
import { RemoveMotionModal } from './components/RemoveMotionModal';
import { RequestMotionItem } from './components/RequestMotionItem';
import { PermitObjectStatus } from '../../../../../types';

type RequestMotionProps = {
  id: string;
  history: MovementType[];
  updateData: () => void;
  cardData: PharmPermissionDTO<FileType>;
  permissions: FunctionPermissions;
};

export const RequestMotion = ({
  id,
  history,
  updateData,
  cardData,
  permissions,
}: RequestMotionProps) => {
  const [functions, setFunctions] = useState<ObjectOperation[]>([]);
  const [removeItemId, setRemoveItemId] = useState<string>('');
  const [isAddMotionModalOpen, setIsAddMotionModalOpen] = useState(false);
  const [isRemoveMotionModalOpen, setIsRemoveMotionModalOpen] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const lastStatusDate =
    history && history.length > 0 ? history[history.length - 1].statusDate : undefined;
  const tempValidDate = cardData?.permissionTempInformation
    ? cardData.permissionTempInformation.permissionTempValidDate
    : undefined;

  const fetchFunctions = async () => {
    const params = {
      objectTypeId: ObjectTypeId.PHARM_PERMISSION,
      withObjectStatusModel: true,
      permissionList: permissions.patentee ? ROLES.patenteePharmPerm : ROLES.rshsnPharmPerm,
      currentObjectStatusId: cardData.status!.id,
    };
    const res = await dictApi.getObjectOperations(params);
    setFunctions(res);
  };

  useEffect(() => {
    fetchFunctions();
  }, []);

  const closeAddModal = () => {
    setIsAddMotionModalOpen(false);
  };

  const openAddModal = () => {
    setIsAddMotionModalOpen(true);
  };

  const closeRemoveModal = () => {
    setRemoveItemId('');
    setIsRemoveMotionModalOpen(false);
  };

  const openRemoveModal = (item: MovementType) => {
    setRemoveItemId(item.id);
    setIsRemoveMotionModalOpen(true);
  };

  const onAddRequestMotion = async (data: MovementData<File[]>) => {
    let rejectFile;
    try {
      rejectFile = await normalizeFile(
        BackendFileType.PHARMACEUTICAL_PERMISSION_DOCS,
        data.rejectFile
      );
    } catch (e) {
      setUpdateError(e.message);
      return;
    }

    const formattedData = {
      ...data,
      rejectFile,
    };
    pharmPermissionsApi
      .createMovement(formattedData, id)
      .then(() => {
        updateData();
        closeAddModal();
      })
      .catch((e) => {
        setUpdateError(e.response.data.message);
        closeAddModal();
      });
  };

  const onRemoveRequestMotion = async () => {
    pharmPermissionsApi
      .deleteMovement(id, removeItemId)
      .then(() => {
        updateData();
        closeRemoveModal();
      })
      .catch((e) => {
        setUpdateError(e.response.data.message);
        closeRemoveModal();
      });
  };

  return (
    <>
      <SubHeader
        header="Движения заявки"
        icon="exchange"
        iconColor="orange"
        iconSize={100}
        level={4}
        toolbar={
          functions?.length &&
          (permissions.rshsn ? (
            <Button color="info" size="minier" text="Добавить" icon="plus" onClick={openAddModal} />
          ) : (
            functions.map((el: ObjectOperation) => (
              <Button
                key={el.id}
                color="info"
                size="minier"
                text={el.name}
                icon="plus"
                onClick={openAddModal}
              />
            ))
          ))
        }
      />
      <div className="mt-15">
        {updateError && (
          <Alert closable messageType="danger">
            {updateError}
          </Alert>
        )}

        {history ? (
          <Timeline
            dateField="statusDate"
            nameField="status.name"
            defaultNameField="status.name"
            subtitle="operation.name"
            data={history}
            onRemove={openRemoveModal}
            allowRemove={
              (cardData.status?.code === PermitObjectStatus.REJECTED ||
                cardData.status?.code === PermitObjectStatus.GRANTED ||
                cardData.status?.code === PermitObjectStatus.EXPIRED) &&
              permissions.rshsn
            }
            onlyLastRemove
            render={(item) => !!item && <RequestMotionItem item={item} permissions={permissions} />}
          />
        ) : (
          <Alert messageType="info">
            <LoadingSpinner
              color="blue"
              icon="spinner"
              spin="spin"
              text="Информация загружается..."
            />
          </Alert>
        )}

        {isAddMotionModalOpen && (
          <AddMotionModal
            onClose={closeAddModal}
            requestFunctions={functions}
            onSubmit={onAddRequestMotion}
            permissions={permissions}
            lastStatusDate={lastStatusDate}
            tempValidDate={tempValidDate}
          />
        )}

        {isRemoveMotionModalOpen && (
          <RemoveMotionModal onClose={closeRemoveModal} onRemove={onRemoveRequestMotion} />
        )}
      </div>
    </>
  );
};
